// Story 7.16 (ADO-1373) — the Stock Card body, shared by BOTH surfaces that show
// one: the PDP accordion (part A, `ProductPanels`) and the category-listing
// popover (part B, `ProductCard` → `design-system/Popover`). One component, so the
// two can never tell the same store two different stories about the same product.
//
// It closed gap G2: the week table was a SKU-hash fixture until Story 14.15 (ADO-1376) landed the store EPOS sales + deliveries
// ingest and its `GET /api/v1/store-stock/stock-card` read. Those fixtures are
// deleted, not merely unused (`productDetailSample.ts`).
//
// ── What it reads, and from where ──
//   • `week` — the Stock Card read (`stock-card.client.ts`),
//     fetched ON MOUNT, which is on OPEN: the PDP accordion unmounts its body when
//     collapsed and the popover mounts on click. A listing page renders 20+ cards
//     and at most one Stock Card is open, so there is deliberately NO eager
//     per-card fetch (Open Question 3 — the card's always-visible teaser stays as
//     it is).
//   • `storeCode` — `useSessionStore.customerAccountCode` (part C). `null` when
//     unprovisioned, which is the normal case today (ADO #1700).
//   • `eanCodes` — the product's `ProductDetail.ean`, via `useProductDetailStore`.
//     The PDP page has already loaded it; the LISTING has not (a listing card
//     carries a `ProductSummary`, which has no `ean` by backend Deviation D-4), so
//     this component calls the store's idempotent `loadDetail` itself. That is the
//     one extra request the popover costs, and it is cached per SKU thereafter.
//   • `N in store` — `useAvailabilityStore`, UNCHANGED from Story 7.3. Both
//     surfaces read that one source (see `ProductPanels`' Availability row and
//     `ProductCard`'s teaser), so the figure cannot diverge between them. Enabler E7-B
//     (ADO-2273, AC15): the count prints only where the active entry shows a quantity
//     (`availability/showsStockQuantity.ts`); elsewhere the badge reads "In stock" or is
//     left out (see `badgeText`).
//
// ── AC5, "absent data → row hidden, no error" ──
// Every absence renders as omission and NEVER as an error banner:
//   • no store code / no EAN            → no read is issued at all, no table
//   • the read failed or 400'd          → `fetchStockCard` resolves null, no table
//   • a movement type with no movement  → that row is dropped (see below)
// The ONE thing that still renders in those states is the `N in store` badge,
// because it comes from a DIFFERENT source that answered. Suppressing a correct,
// already-shipping Story 7.3 figure because an unrelated read found nothing would
// be a regression, not AC5 compliance (Deviation D-6).
//
// ── Why rows are filtered here rather than server-side ──
// The read ALWAYS returns eight rows (`stock-card-aggregation.ts:147-159`):
// Sales, Sales Value, Sales 4wk Avg, Sales 4wk Avg Value, and the four Purchases
// equivalents — including all-`null` rows for a movement type with no movement.
// So two filters run here, and both are deliberate (Deviation D-7):
//   1. Only the AC1/AC2 rows are DISPLAYED — `Sales` and `Purchases`. The ACs name
//      exactly those two and so does the approved design (Figma 3575:10456 /
//      1361:7370). The Value series are money, and the DTO carries them as bare
//      numbers with no currency, which the repo's Money rule forbids rendering as
//      a price; the 4wk-average series has no home in either design. Both remain
//      available server-side for a future story that designs a place for them.
//   2. An all-`null` row is DROPPED, which is AC5's "row hidden". The row's mere
//      presence in the response is not evidence of movement.

import { useEffect, useState } from 'react';
import { StatusBadge } from '../../design-system';
import {
  fetchStockCard,
  type StockCardDto,
  type StockCardWeekRow,
} from '../../api/stock-card.client';
import { useAvailabilityStore } from '../../availability/useAvailabilityStore';
import {
  IN_STOCK_LABEL,
  isInStockForLabel,
  selectShowsStockQuantity,
} from '../../availability/showsStockQuantity';
import { useProductDetailStore } from '../../product-detail/useProductDetailStore';
import { useSessionStore } from '../../auth/useSessionStore';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import styles from './StockCardContent.module.css';

/** Column headers for the Mon→Sun series the read returns (7 entries, index 0 = Mon). */
export const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

/**
 * The movement rows this card DISPLAYS, in render order — the AC1 (Sales) and AC2
 * (Purchases) rows. Matched against the server's `StockCardWeekRow.label`.
 *
 * Exported so `StockCardContent.spec.tsx` can pin the pairing against the labels
 * `stock-card-aggregation.ts` actually emits: a rename on either side must fail a
 * test rather than silently render an empty table. An unrecognised label is simply
 * not shown, which is the safe direction (absent, never wrong).
 */
export const DISPLAYED_MOVEMENT_ROWS: readonly string[] = ['Sales', 'Purchases'];

export interface StockCardContentProps {
  readonly sku: string;
  /** Names the table for assistive tech, and heads the card on both surfaces. */
  readonly productName: string;
}

/** AC5 — a row with no movement on any weekday is omitted rather than rendered as
 *  seven em dashes. `total` is not consulted: the days are the evidence. */
function hasMovement(row: StockCardWeekRow): boolean {
  return row.days.some((value) => value !== null);
}

export function StockCardContent({ sku, productName }: StockCardContentProps) {
  const storeCode = useSessionStore((s) => s.customerAccountCode);
  // Story 7.3 — the SESSION depot's REAL stock on hand, when the store has it.
  const availability = useAvailabilityStore((s) => s.entries[sku]);
  // Enabler E7-B (ADO-2273), AC15 restated by D-15: whether the badge may print the count.
  const showsQuantity = useSessionStore(selectShowsStockQuantity);
  const detail = useProductDetailStore((s) => s.detailBySku[sku]?.detail ?? null);
  const detailPhase = useProductDetailStore((s) => s.detailBySku[sku]?.phase);
  const loadDetail = useProductDetailStore((s) => s.loadDetail);

  const [stockCard, setStockCard] = useState<StockCardDto | null>(null);

  // Resolve the product's EAN. Idempotent and cached per SKU; on the PDP the page
  // has already claimed this request (`load` stamps both slices before it awaits),
  // so this stands down there and only really fires for the listing popover.
  // Enabler E7-C (ADO-2274), D-1: an entry change resets the detail cache; read it again.
  const catalogueEntryKey = useCatalogueEntryKey();
  useEffect(() => {
    void loadDetail(sku);
  }, [sku, loadDetail, catalogueEntryKey]);

  const ean = detail?.ean ?? null;
  // Wait for the detail read to SETTLE before concluding there is no EAN: firing
  // on a still-loading entry would read `ean` as null and skip the fetch for a
  // product that has one. `loaded` is the only phase that can carry a detail row;
  // `notFound`/`error` settle with none, and correctly issue no read.
  const detailSettled = detailPhase !== undefined && detailPhase !== 'loading';

  useEffect(() => {
    // Reset FIRST, unconditionally: `sku` can change on an already-mounted
    // instance without a remount (the PDP has no `key={sku}`, so a
    // recommendation-rail navigation reuses this component), and if the
    // destination product's EAN is already cached the guard below is skipped on
    // the very next render. Without this reset the PREVIOUS product's week table
    // would stay on screen, under the new product's name, until the new fetch
    // resolves.
    setStockCard(null);
    // Guards, not duplicated server validation: with no store or no EAN the read
    // would 400, and there is nothing to ask about anyway (AC5).
    if (storeCode === null || !detailSettled || ean === null) {
      return;
    }
    let cancelled = false;
    void fetchStockCard({
      storeCode,
      productCode: sku,
      // Deviation D-4: the client holds ONE barcode (`ProductDetail.ean`, SL1.0
      // `EAN1`), where SL1.0's own query joins on `EAN1|EAN2|EAN3`. Strictly
      // narrower, documented, and not claimed as parity.
      eanCodes: [ean],
    }).then((result) => {
      // A depot/session change or a close-then-reopen can land a stale response
      // after this effect has been torn down; dropping it is what stops one
      // product's figures appearing under another's name.
      if (!cancelled) setStockCard(result);
    });
    return () => {
      cancelled = true;
    };
  }, [storeCode, sku, ean, detailSettled]);

  const rows = (stockCard?.week ?? [])
    .filter((row) => DISPLAYED_MOVEMENT_ROWS.includes(row.label))
    .filter(hasMovement);

  // The badge prints the count where the active entry shows a quantity. Elsewhere it reads
  // SL1.0's "In stock" on a band with stock, and is left out on the out-of-stock band or a
  // supplier block, whose pill already says so (E7-B AC15, ADO-2273).
  const badgeText =
    availability === undefined
      ? null
      : showsQuantity
        ? // `Math.max(0, …)` mirrors the grid card's guard against a negative figure.
          `${Math.max(0, availability.quantityOnHand)} in store`
        : isInStockForLabel(availability)
          ? IN_STOCK_LABEL
          : null;
  const inStoreBadge = badgeText !== null && (
    <StatusBadge tone="success" dot={false}>
      {badgeText}
    </StatusBadge>
  );
  // Nothing answered — no badge, no table. Render NOTHING rather than a heading
  // over an empty box (AC5).
  if (inStoreBadge === false && rows.length === 0) return null;

  return (
    <div className={styles.stockCard}>
      <div className={styles.stockHeader}>
        <p className={styles.stockTitle}>{productName}</p>
        <div className={styles.stockBadges}>{inStoreBadge}</div>
      </div>

      {rows.length > 0 && (
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <caption className={styles.visuallyHidden}>
              Weekly sales and purchases for {productName}
            </caption>
            <thead>
              <tr>
                <th scope="col" className={styles.rowHeadCell}>
                  <span className={styles.visuallyHidden}>Movement</span>
                </th>
                {DAY_LABELS.map((day) => (
                  <th key={day} scope="col">
                    {day}
                  </th>
                ))}
                <th scope="col">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <th scope="row" className={styles.rowHeadCell}>
                    {row.label}
                  </th>
                  {/* Day columns are a fixed, ordered Mon–Sun series, so the day
                      label is a stable key (the array never reorders). */}
                  {row.days.map((value, i) => (
                    <td key={`${row.label}-${DAY_LABELS[i] ?? i}`}>{value ?? '–'}</td>
                  ))}
                  <td className={styles.totalCell}>{row.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
